import { useState } from 'react';
import { Button } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { Sliders } from 'react-feather';
import { useNavigate } from 'react-router-dom';
import {
  matchingSessionsApi,
  type MatchingReadiness,
} from '../../../../services/api/matchingSessions';
import type { MatchingPageState } from '../../matching/MatchingPage';
import { DisabledReasonTooltip } from './DisabledReasonTooltip';
import {
  deriveStartErrorMessage,
  isStartDisabled,
  startDisabledReasons,
} from './matchingStart';

interface SetUpMatchingButtonProps {
  cohortId: string;
  groupsPrereqsMet: boolean;
  readiness: MatchingReadiness | undefined;
}

/**
 * "Set Up Matching": creates a matching session (same endpoint as Start
 * Matching) and opens the in-app matching tool for it instead of the
 * matching-server tab. The create call's `matching_url` rides along in router
 * state so the first WebSocket connect doesn't need a re-mint.
 *
 * Same gating, tooltip and error messages as StartMatchingButton.
 */
export function SetUpMatchingButton({
  cohortId,
  groupsPrereqsMet,
  readiness,
}: SetUpMatchingButtonProps) {
  const navigate = useNavigate();
  const [isCreating, setIsCreating] = useState(false);
  const isDisabled = isStartDisabled(groupsPrereqsMet, readiness);

  const handleClick = async () => {
    setIsCreating(true);
    try {
      const response = await matchingSessionsApi.start(cohortId);
      const state: MatchingPageState = { matchingUrl: response.matching_url };
      navigate(`/cohorts/${cohortId}/matching/${response.session_id}`, {
        state,
      });
    } catch (err) {
      notifications.show({
        color: 'red',
        title: "Couldn't set up matching",
        message: deriveStartErrorMessage(err),
      });
      setIsCreating(false);
    }
  };

  return (
    <DisabledReasonTooltip
      reasons={
        isDisabled ? startDisabledReasons(groupsPrereqsMet, readiness) : []
      }
    >
      <Button
        variant="outline"
        leftSection={<Sliders size={18} />}
        onClick={handleClick}
        loading={isCreating}
        disabled={isDisabled}
      >
        Set Up Matching
      </Button>
    </DisabledReasonTooltip>
  );
}
